import { Paperclip } from "react-bootstrap-icons";
import moment from "moment";
import {
  getSenderName,
  isOutgoingMessageForIcon,
} from "../../../../../utils/helperUtil";
import {
  DocumentIcon,
  InvoiceIcon,
  MailInCommingIcon,
  MailOutCommingIcon,
  MassmailGreen,
  NoticeIcon,
  PhoneInComingIcon,
  PhoneInOutGoingIcon,
  PhoneMissedIcon,
  TaskIcon,
} from "../../../../../components/protected/Icons/IconsLib";
import styles from "./messages_card_mobile.module.css";
import { BulkSelectCheckboxSlot } from "../../../../../components/protected/layout-components/BulkSelectCheckbox";

interface IMessagesCardComponentMobileProps {
  item: any;
  showUnresolvedReferenceIndicator?: boolean;
}

const MessagesCardComponentMobile: React.FC<IMessagesCardComponentMobileProps> = ({
  item,
  showUnresolvedReferenceIndicator = false,
}) => {
  const rowId = String(item?.id ?? "");
  const showFollowReferenceBadge =
    showUnresolvedReferenceIndicator &&
    String(item?.followReference ?? "") === "0";
  const extractValues = (data: any, prop: string): string[] => {
    if (Array.isArray(data)) {
      return data
        .map((entry) => entry?.[prop])
        .filter(Boolean)
        .map((value) => String(value).trim());
    }
    if (data && typeof data === "object" && data[prop]) {
      return [String(data[prop]).trim()];
    }
    return [];
  };

  const extractFirstAvailable = (data: any, props: string[]): string[] => {
    for (const p of props) {
      const values = extractValues(data, p);
      if (values.length > 0) return values;
    }
    return [];
  };

  const toRecipients = extractValues(item?.RECIPIENT?.TO, "CONTENT");
  const ccRecipients = extractValues(item?.RECIPIENT?.CC, "CONTENT");
  const bccRecipients = extractValues(item?.RECIPIENT?.BCC, "CONTENT");
  const notifyRecipients = extractFirstAvailable(item?.RECIPIENT?.USER, [
    "contact",
    "contactName",
    "CONTENT",
    "userName",
    "name",
    "id",
  ]);

  const primaryRecipient =
    toRecipients[0] ||
    ccRecipients[0] ||
    bccRecipients[0] ||
    notifyRecipients[0] ||
    "";

  const secondaryRecipientLine = [
    ...ccRecipients,
    ...bccRecipients,
    ...notifyRecipients,
    ...toRecipients.slice(1),
  ]
    .filter(Boolean)
    .join(", ");

  const renderTypeIcon = () => {
    switch (item.type) {
      case "1":
      case "12":
        return <NoticeIcon size={24} />;
      case "2":
      case "3":
      case "7":
        return isOutgoingMessageForIcon(item) ? (
          <MailOutCommingIcon size={24} />
        ) : (
          <MailInCommingIcon size={24} />
        );
      case "9":
      case "14":
      case "15":
      case "11":
        return <MailOutCommingIcon size={24} />;
      case "4":
      case "5":
        return <DocumentIcon size={24} />;
      case "6":
        if (item.status === "131072") {
          return <PhoneInComingIcon size={24} height={16} />;
        }
        if (item.status === "262144") {
          return <PhoneInOutGoingIcon size={24} height={16} />;
        }
        return <PhoneMissedIcon size={24} height={16} />;
      case "8":
        return <MassmailGreen size={24} />;
      case "10":
        return <TaskIcon size={24} />;
      case "13":
        return <InvoiceIcon size={24} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={styles.cardRoot}
      style={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        height: 81,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8,
        padding: "8px 6px",
      }}
    >
      <div
        className={styles.iconLane}
        style={{
          width: 24,
          minWidth: 24,
          height: 24,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          overflow: "visible",
          position: "relative",
        }}
      >
        {renderTypeIcon()}
        {showFollowReferenceBadge && (
          <span className={styles.followReferenceBadge}>?</span>
        )}
      </div>
      <div
        className={styles.contentLane}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: 2,
        }}
      >
        <div
          className={styles.topRow}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            minWidth: 0,
            width: "100%",
          }}
        >
          <span
            className={item?.seen === "1" ? styles.senderRead : styles.senderUnread}
            title={item?.sender}
            style={{
              fontSize: 15,
              lineHeight: "22px",
              color: "#36474f",
              fontWeight: item?.seen === "1" ? 400 : 700,
              minWidth: 0,
              maxWidth: "40%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 1,
            }}
          >
            {getSenderName(item?.sender)}
          </span>
          {primaryRecipient && (
            <span
              className={styles.divider}
              style={{ color: "#dee4e7", flexShrink: 0 }}
            >
              |
            </span>
          )}
          {primaryRecipient && (
            <span
              className={styles.primaryRecipient}
              title={primaryRecipient}
              style={{
                fontSize: 11,
                lineHeight: "18px",
                color: "#728289",
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: "1 1 auto",
              }}
            >
              {primaryRecipient}
            </span>
          )}
        </div>
        <div
          className={item?.seen === "1" ? styles.subjectRead : styles.subjectUnread}
          title={item?.subject}
          style={{
            fontSize: 14,
            lineHeight: "20px",
            color: "#36474f",
            fontWeight: item?.seen === "1" ? 400 : 700,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item?.subject || ""}
        </div>
        <div
          className={styles.secondaryRecipients}
          title={secondaryRecipientLine}
          style={{
            fontSize: 11,
            lineHeight: "16px",
            color: "#728289",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {secondaryRecipientLine}
        </div>
      </div>
      <div
        className={styles.metaLane}
        style={{
          width: 72,
          minWidth: 72,
          flex: "0 0 72px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "space-between",
          minHeight: 60,
        }}
      >
        <div
          className={styles.attachmentWrap}
          style={{
            minHeight: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            color: "#728289",
          }}
        >
          {item?.numberOfAttachments > 0 && <Paperclip size={12} />}
        </div>
        <div
          className={styles.dateText}
          style={{
            fontSize: 11,
            lineHeight: "14px",
            color: "#728289",
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {moment(item?.date).format("DD.MM.YYYY HH:mm")}
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
        }}
      >
        <BulkSelectCheckboxSlot rowId={rowId} />
      </div>
    </div>
  );
};

export default MessagesCardComponentMobile;
